import { IoArrowUp, IoArrowDown } from 'react-icons/io5'
import type { PlayerStatsTab } from '../../types'

export type PlayerStatsFilters = {
  daysAhead: number
  leagueIds: number[]
  fixtureId: number | null
  position: string | null
  minProbability: number | null
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

const DATE_PRESETS = [
  { value: 1, label: 'Today' },
  { value: 2, label: '2 Days' },
  { value: 3, label: '3 Days' },
  { value: 5, label: '5 Days' },
  { value: 7, label: '7 Days' },
]

const POSITION_OPTIONS = [
  { value: null, label: 'All' },
  { value: 'attacker', label: 'Attackers' },
  { value: 'midfielder', label: 'Midfielders' },
  { value: 'defender', label: 'Defenders' },
]

const SORT_OPTIONS_BY_TAB = {
  shots: [
    { value: 'probability', label: 'Probability' },
    { value: 'kickoff', label: 'Kickoff' },
    { value: 'last5_avg', label: 'SOT (L5)' },
    { value: 'expected_mins', label: 'Minutes' },
  ],
  fouls_cards: [
    { value: 'fouls_committed', label: 'Fouls' },
    { value: 'fouls_drawn', label: 'Drawn' },
    { value: 'cards', label: 'Cards' },
    { value: 'tackles', label: 'Tackles' },
    { value: 'kickoff', label: 'Kickoff' },
    { value: 'ref_factor', label: 'Ref Factor' },
  ],
}

interface PlayerStatsFilterBarProps {
  filters: PlayerStatsFilters
  onChange: (filters: PlayerStatsFilters) => void
  activeTab?: PlayerStatsTab
}

export function PlayerStatsFilterBar({ filters, onChange, activeTab = 'shots' }: PlayerStatsFilterBarProps) {
  // Get sort options based on active tab
  const sortOptions = SORT_OPTIONS_BY_TAB[activeTab]

  return (
    <div className="player-stats-filter-bar">
      {/* Date presets */}
      <div className="filter-row">
        <label>Time range:</label>
        <div className="filter-chips">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              className={`chip ${filters.daysAhead === preset.value ? 'chip-active' : ''}`}
              onClick={() => onChange({ ...filters, daysAhead: preset.value })}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Position filter */}
      <div className="filter-row">
        <label>Position:</label>
        <div className="filter-chips">
          {POSITION_OPTIONS.map((opt) => (
            <button
              key={opt.value || 'all'}
              className={`chip ${filters.position === opt.value ? 'chip-active' : ''}`}
              onClick={() => onChange({ ...filters, position: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sort options as chips */}
      <div className="filter-row filter-row-sort">
        <label>Sort by:</label>
        <div className="filter-chips-with-order">
          <div className="filter-chips">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                className={`chip ${filters.sortBy === opt.value ? 'chip-active' : ''}`}
                onClick={() => onChange({ ...filters, sortBy: opt.value })}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            className={`chip sort-order-chip ${filters.sortOrder === 'desc' ? 'chip-active' : ''}`}
            onClick={() =>
              onChange({
                ...filters,
                sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc',
              })
            }
            title={filters.sortOrder === 'desc' ? 'Descending (click for ascending)' : 'Ascending (click for descending)'}
          >
            {filters.sortOrder === 'desc' ? <IoArrowDown size={14} /> : <IoArrowUp size={14} />}
          </button>
        </div>
      </div>
    </div>
  )
}
